import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import Player from 'engage/components/Player';
import { useStoryboardContext } from 'engage/context/StoryboardContext';
import styles from './ScenePlayer.module.scss';
import ResourceHelper from 'engage/utils/ResourceHelper';
import RecordClipTrigger from 'engage/components/RecordClipTrigger/RecordClipTrigger';
import { useTranslation } from 'react-i18next';
import { useUserContext } from 'engage/context/UserContext';

export const ScenePlayer = ({
  isRecording,
  cancelRecording,
  startRecording,
  startUploading,
  startAvatar,
  collapse
}) => {
  const { currentScene, scenes, userPermissions } = useStoryboardContext();

  const { isAvatarEnabled } = useUserContext();

  const { t } = useTranslation();

  const scene = useMemo(() => scenes[Number(currentScene)]);

  const editorRef = useRef(null);

  const thumbnail = useMemo(() => {
    if (scene) {
      const thumbnailResource = ResourceHelper.getResourceByLabel(scene, 'defaultStaticThumbnail');
      if (thumbnailResource) {
        return thumbnailResource.url;
      }
    }
    return null;
  }, [scene])

  const record = useCallback(() => {
    startRecording(scene?.id);
  }, [startRecording, scene]);

  const upload = useCallback(() => {
    startUploading(scene?.id);
  }, [startUploading, scene]);

  const avatar = useCallback(() => {
    startAvatar(scene?.id);
  }, [startAvatar, scene]);

  const avatarDisabledTooltip = useMemo(() => {
    if (scene?.script?.length === 0) {
      return t('clip_organizer.generate_avatar.avatar_disabled_tooltip');
    }
    return '';
  }, [scene, t]);

  const isDraftActionsAllowed = useMemo(() => {
    if ((scene?.recording || (!scene?.video && !scene?.trimmedDuration)) && !scene?.processing) {
      const { allowedScenes, updateStory } = userPermissions;
      if (updateStory) {
        return true;
      }
      if (allowedScenes.length > 0) {
        return allowedScenes?.find(item => item === scene?.id) !== undefined;
      }
    }
    return false;
  }, [userPermissions, scene]);

  const draftComponent = useMemo(() => {
    const actions = [];
    if ((scene?.recording || (!scene?.video && !scene?.trimmedDuration)) && !scene?.processing) {
      const { allowedScenes, updateStory } = userPermissions;
      if (updateStory || allowedScenes?.find(item => item === scene?.id) !== undefined) {
        actions.push({ label: t('storyboard.editor.record_button'), onClick: record, disabled: isRecording });
        actions.push({ label: t('clip_organizer.add_modal.upload'), onClick: upload, disabled: isRecording });
        if (isAvatarEnabled) {
          actions.push({ label: t('clip_organizer.add_modal.avatar'), onClick: avatar, disabled: isRecording || !scene?.script?.length, disabledTooltip: avatarDisabledTooltip });
        }
      }
      return <RecordClipTrigger
        actions={actions}
        isRecording={isRecording}
        message={t('storyboard.editor.clip_status.not_recorded')}
        handleCancel={cancelRecording}
      />
    }
    return null;
  }, [
    isRecording,
    scene,
    avatarDisabledTooltip,
    isRecording,
    record,
    upload,
    avatar,
    cancelRecording,
    t,
    isDraftActionsAllowed
  ]);

  const sceneCouldBeRerecorded = true;

  const rerecordComponent = useMemo(() => <RecordClipTrigger
    actions={[
      { label: t('storyboard.editor.record_button'), onClick: record, disabled: (!sceneCouldBeRerecorded) || isRecording },
      { label: t('clip_organizer.add_modal.upload'), onClick: upload, disabled: (!sceneCouldBeRerecorded) || isRecording },
      ...(isAvatarEnabled ? [{ label: t('clip_organizer.add_modal.avatar'), onClick: avatar, disabled: (!sceneCouldBeRerecorded) || isRecording || !scene?.script?.length, disabledTooltip: avatarDisabledTooltip }] : [])
    ]}
    isRecording={isRecording}
    message={!sceneCouldBeRerecorded ? t('clip_organizer.save_to_record_message') : ''}
    disableClassName={styles.disableRerecordButton}
    handleCancel={cancelRecording}
  />, [isRecording, scene, t, cancelRecording, record,
    upload,
    avatar,]);

  const isRerecordComponentAllowed = useMemo(() => {
    if (scene?.video) {
      const { allowedScenes, updateStory } = userPermissions;
      let isAllowed = updateStory;
      if (!isAllowed && allowedScenes.length > 0) {
        isAllowed = allowedScenes?.find(item => item === scene?.id) !== undefined;
      }
      return isAllowed;
    }
  }, [userPermissions, scene]);

  useEffect(() => {
    if (collapse) {
      // Set the height of the player wrapper and only after that set it to 0. This is needed due to 
      // transitions is browser doesn't word from 0 to auto and vise versa.
      editorRef.current.style.height = `${editorRef.current.scrollHeight}px`;
      setTimeout(() => {
        editorRef.current.style.height = '0px'; // Collapse to zero height
      }, 0)
    } else {
      // Calculate the height of the player wrapper based on the content
      const wrapperHeight = editorRef.current.scrollHeight;
      editorRef.current.style.height = `${wrapperHeight}px`;
      setTimeout(() => {
        editorRef.current.style.height = 'auto'; // Reset to auto to allow for dynamic content changes
      }, 250); // Allow some time for the transition to take effect
    }
  }, [collapse])


  return (
    <div ref={editorRef} className={styles.playerWrapper}>
      <Player
        src={scene?.recording ? null : scene?.video}
        uuid={scene?.id}
        overlay={true}
        active={true}
        poster={scene?.recording ? null : thumbnail}
        isDraft={draftComponent}
        processing={scene?.processing ?? null}
        fadeIn={false}
        blockPlaying={isRecording || Boolean(scene?.recording) || Boolean(scene?.processing)}
        hidePlayControl={!scene?.video}
        rerecordComponent={isRerecordComponentAllowed ? React.cloneElement(
          rerecordComponent,
        ) : null}
      />
    </div>
  )
}